import { useState, useEffect } from 'react'
import { Table, Button, Space, message, Card, Tabs, Popconfirm, Tag, Form, Input, Row, Col, Checkbox, ColorPicker } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useAuth } from '@/contexts/AuthContext'
import { UserFormModal } from './UserFormModal'
import { useWorkshops } from '@/hooks/useWorkshops'
import { SystemTableManager } from '@/components/SystemTableManager'
import { InterventionStatusTypeManager } from '@/components/InterventionStatusTypeManager'
import { InsuranceBranchTypeManager } from '@/components/InsuranceBranchTypeManager'
import { RolePermissionsMatrix } from '@/components/RolePermissionsMatrix'
import { PermissionsByRole } from '@/components/PermissionsByRole'
import { RolesManager } from '@/components/RolesManager'
import { DatabaseBackupManager } from '@/components/DatabaseBackupManager'
import { RDAWorkflowDiagramEditor } from '@/components/RDAWorkflowDiagramEditor'
import { WorkOrderWorkflowDiagramEditor } from '@/components/WorkOrderWorkflowDiagramEditor'
import { GoogleCalendarSettings } from '@/components/GoogleCalendarSettings'
import { GoogleAPISettingsGuide } from '@/components/GoogleAPISettingsGuide'
import { AnthropicSettings } from '@/components/AnthropicSettings'
import { SearchPortalConfig } from '@/components/SearchPortalConfig'
import MessaggiTemplateManager from '@/components/MessaggiTemplateManager'
import IntegrazioniSettings from '@/components/IntegrazioniSettings'
import ActivityLogPanel from '@/components/ActivityLogPanel'
import MarcheModelliSettings from '@/components/settings/MarcheModelliSettings'
import AdcClausoleSettings from '@/components/settings/AdcClausoleSettings'

interface User {
  id: number
  email: string
  nome?: string
  cognome?: string
  username?: string
  ruolo: string
  ruolo_id?: number
  workshop_id?: number | null
  ux_preference?: string
  attivo: boolean
  created_at: string
  updated_at: string
}


const SettingsPage = () => {
  const { setTitle } = usePageTitle()
  const { user: currentUser, refreshUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [rolesRefreshKey, setRolesRefreshKey] = useState(0)
  const { data: workshopsData } = useWorkshops(undefined)
  const workshops = workshopsData?.items ?? []

  // Set page title
  useEffect(() => {
    setTitle('⚙️ Impostazioni Sistema')
  }, [setTitle])

  // Load users on component mount
  useEffect(() => {
    loadUsers()
  }, [])

  const handleRolesChanged = () => {
    // Incrementa il trigger per forzare refetch in RolePermissionsMatrix
    setRolesRefreshKey(prev => prev + 1)
  }

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get<{ items?: User[]; _items?: User[] }>(
        API_ENDPOINTS.USERS
      )
      // Handle both possible response formats
      const userData = response.data.items || response.data._items || response.data || []
      setUsers(Array.isArray(userData) ? userData : [])
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error)
      message.error('Errore nel caricamento utenti')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingUser(null)
    setIsModalOpen(true)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setIsModalOpen(true)
  }

  const handleDelete = async (userId: number) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.USERS}${userId}`)
      message.success('Utente eliminato con successo')
      loadUsers()
    } catch (error) {
      console.error('Errore:', error)
      message.error('Errore durante l\'eliminazione dell\'utente')
    }
  }

  const handleModalOkValues = async (values: any) => {
    try {
      if (editingUser) {
        // Update existing user
        await axiosInstance.put(`${API_ENDPOINTS.USERS}${editingUser.id}`, values)
        message.success('Utente aggiornato con successo')
      } else {
        // Create new user
        await axiosInstance.post(API_ENDPOINTS.USERS, values)
        message.success('Utente creato con successo')
      }

      setIsModalOpen(false)
      setEditingUser(null)
      loadUsers()
      // Se l'utente ha modificato se stesso, ricarica il profilo in AuthContext
      // così ux_preference e altri campi diventano subito attivi senza logout
      if (editingUser && currentUser && editingUser.id === currentUser.id) {
        await refreshUser()
      }
    } catch (error) {
      console.error('Errore:', error)
      message.error('Errore durante il salvataggio dell\'utente')
    }
  }

  const columns: ColumnsType<User> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 140,
    },
    {
      title: 'Nome Completo',
      dataIndex: 'nome',
      key: 'nome',
      render: (_text: any, record: any) => `${record.nome} ${record.cognome}`,
      width: 150,
    },
    {
      title: 'Ruolo',
      dataIndex: 'ruolo',
      key: 'ruolo',
      width: 160,
      render: (_: any, record: any) => <Tag>{record.ruolo_nome ?? record.ruolo}</Tag>
    },
    {
      title: 'Officina',
      dataIndex: 'workshop_id',
      key: 'workshop_id',
      width: 160,
      render: (wid: number | null) => {
        if (!wid) return <span style={{ color: '#bbb' }}>—</span>
        const w = workshops.find((x) => x.id === wid)
        return w ? <Tag color={w.tipo === 'meccanica' ? 'blue' : 'orange'}>{w.nome}</Tag> : wid
      },
    },
    {
      title: 'UX Layout',
      dataIndex: 'ux_preference',
      key: 'ux_preference',
      width: 130,
      render: (v: string) => {
        const map: Record<string, { label: string; color: string }> = {
          'responsive':   { label: '🖥️ Responsive',   color: '#1890ff' },
          'mobile-first': { label: '📱 Mobile-first', color: '#52c41a' },
          'adaptive':     { label: '📐 Adaptive',     color: '#722ed1' },
        }
        const entry = map[v] ?? { label: v ?? '—', color: '#999' }
        return <Tag color={entry.color} style={{ fontSize: 11 }}>{entry.label}</Tag>
      }
    },
    {
      title: 'Attivo',
      dataIndex: 'attivo',
      key: 'attivo',
      width: 80,
      render: (attivo: boolean) => attivo ? '✅' : '❌'
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Eliminare utente?"
            description="Questa azione non può essere annullata"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ]

  const NAV = [
    { key: 'ai',         icon: '🤖', label: 'Intelligenza Artificiale' },
    { key: 'google',     icon: '🔐', label: 'Google Account' },
    { key: 'users',      icon: '👥', label: 'Utenti & Ruoli' },
    { key: 'workflow',   icon: '🔄', label: 'Workflow RDO' },
    { key: 'workflow-wo',icon: '📋', label: 'Workflow Lavori' },
    { key: 'messaggi',   icon: '💬', label: 'Messaggi WhatsApp/SMS' },
    { key: 'backup',       icon: '💾', label: 'Backup Database' },
    { key: 'lookup',       icon: '📊', label: 'Tabelle di Sistema' },
    { key: 'marche-modelli', icon: '🚗', label: 'Marche & Modelli' },
    { key: 'activity-log', icon: '📋', label: 'Log Attività' },
    { key: 'adc-clausole', icon: '🚗', label: 'Clausole ADC' },
  ]

  const [activeSection, setActiveSection] = useState('ai')

  return (
    <div style={{ padding: '24px' }}>
      {/* Navigazione a bottoni — 4 per riga, max 2 righe */}
      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: '12px 16px' }}>
        <Row gutter={[8, 8]}>
          {NAV.map(n => (
            <Col key={n.key} xs={12} sm={6}>
              <Button
                block
                type={activeSection === n.key ? 'primary' : 'default'}
                onClick={() => setActiveSection(n.key)}
                style={{ textAlign: 'left', height: 40, fontWeight: activeSection === n.key ? 600 : 400 }}
              >
                {n.icon} {n.label}
              </Button>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Contenuto sezione attiva */}
      {activeSection === 'ai' && (
        <Card>
          <Tabs defaultActiveKey="anthropic" items={[
            {
              key: 'anthropic',
              label: '🧠 Claude (Anthropic)',
              children: <div style={{ paddingTop: 16 }}><AnthropicSettings /></div>,
            },
            {
              key: 'procurement',
              label: '🛒 Ricerca Mercato',
              children: <div style={{ paddingTop: 16 }}><SearchPortalConfig /></div>,
            },
          ]} />
        </Card>
      )}

      {activeSection === 'google' && (
        <Card>
          <Tabs defaultActiveKey="calendar" items={[
            {
              key: 'calendar',
              label: '📅 Calendario Google',
              children: <div style={{ paddingTop: 16 }}><GoogleCalendarSettings /></div>,
            },
            {
              key: 'api-setup',
              label: '📥 Setup Google Drive API',
              children: <div style={{ paddingTop: 16 }}><GoogleAPISettingsGuide /></div>,
            },
          ]} />
        </Card>
      )}

      {activeSection === 'users' && (
        <Card>
          <Tabs defaultActiveKey="users-list" items={[
            {
              key: 'users-list',
              label: '👥 Utenti',
              children: (
                <div style={{ paddingTop: 16 }}>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="small">
                      Aggiungi Utente
                    </Button>
                  </div>
                  <Table
                    columns={columns}
                    dataSource={users}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                    bordered
                  />
                </div>
              ),
            },
            {
              key: 'roles-management',
              label: '🎯 Gestione Ruoli',
              children: <div style={{ paddingTop: 16 }}><RolesManager onRolesChanged={handleRolesChanged} /></div>,
            },
            {
              key: 'rbac-matrix',
              label: '🔐 Ruoli & Permessi',
              children: <div style={{ paddingTop: 16 }}><RolePermissionsMatrix refreshKey={rolesRefreshKey} /></div>,
            },
            {
              key: 'permessi-per-ruolo',
              label: '👤 Permessi per ruolo',
              children: <div style={{ paddingTop: 16 }}><PermissionsByRole refreshKey={rolesRefreshKey} /></div>,
            },
          ]} />
        </Card>
      )}

      {activeSection === 'workflow' && (
        <Card>
          <RDAWorkflowDiagramEditor />
        </Card>
      )}

      {activeSection === 'workflow-wo' && (
        <WorkOrderWorkflowDiagramEditor />
      )}

      {activeSection === 'messaggi' && (
        <MessaggiTemplateManager />
      )}

      {activeSection === 'backup' && (
        <DatabaseBackupManager />
      )}

      {activeSection === 'lookup' && (
        <Card>
          <Tabs defaultActiveKey="damage-types" items={[
            {
              key: 'workshop-types',
              label: '🏭 Tipi Officina',
              children: (
                <SystemTableManager
                  title="Tipo Officina"
                  endpoint={API_ENDPOINTS.SYSTEM_WORKSHOP_TYPES}
                  extraFormFields={<>
                    <Form.Item name="codice" label="Codice" rules={[{ required: true, message: 'Codice obbligatorio' }]}>
                      <Input placeholder="es. meccanica" />
                    </Form.Item>
                    <Form.Item name="colore" label="Colore tag" tooltip="Nome colore Ant Design: blue, orange, green, red, purple, cyan…">
                      <Input placeholder="es. blue" />
                    </Form.Item>
                  </>}
                  columns={[
                    { title: 'Codice', dataIndex: 'codice', key: 'codice', width: 130 },
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Colore', dataIndex: 'colore', key: 'colore', width: 100, render: (colore: string) => <Tag color={colore}>{colore}</Tag> },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                />
              ),
            },
            {
              key: 'damage-types',
              label: '🔧 Tipi di Danno',
              children: (
                <SystemTableManager
                  title="Tipo di Danno"
                  endpoint={API_ENDPOINTS.SYSTEM_DAMAGE_TYPES}
                  columns={[
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                />
              ),
            },
            {
              key: 'customer-types',
              label: '👤 Tipi di Cliente',
              children: (
                <SystemTableManager
                  title="Tipo di Cliente"
                  endpoint={API_ENDPOINTS.SYSTEM_CUSTOMER_TYPES}
                  columns={[
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                />
              ),
            },
            {
              key: 'status-types',
              label: '📊 Stati Scheda Lavori',
              children: (
                <SystemTableManager
                  title="Stato Scheda"
                  endpoint={API_ENDPOINTS.SYSTEM_WORKORDER_STATUS_TYPES}
                  readOnlyNomeOnEdit
                  columns={[
                    { title: 'Ordine', dataIndex: 'ordine', key: 'ordine', width: 80, sorter: (a: any, b: any) => a.ordine - b.ordine, defaultSortOrder: 'ascend' as const },
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Colore', dataIndex: 'colore', key: 'colore', width: 70, render: (v: string) => v ? <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 3, background: v, border: '1px solid #d9d9d9', verticalAlign: 'middle' }} title={v} /> : '—' },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Richiede commento', dataIndex: 'richiede_commento', key: 'richiede_commento', width: 130, render: (v: boolean) => v ? '✅' : '—' },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                  extraFormFields={
                    <>
                      <Form.Item
                        name="colore"
                        label="Colore"
                        normalize={(v) => {
                          if (!v) return undefined
                          if (typeof v === 'string') return v
                          return typeof (v as any).toHexString === 'function' ? (v as any).toHexString() : String(v)
                        }}
                        style={{ marginBottom: 8 }}
                      >
                        <ColorPicker format="hex" allowClear />
                      </Form.Item>
                      <Form.Item name="richiede_commento" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>Richiede commento al cambio stato</Checkbox>
                      </Form.Item>
                    </>
                  }
                />
              ),
            },
            {
              key: 'priority-types',
              label: '⚡ Priorità',
              children: (
                <SystemTableManager
                  title="Priorità"
                  endpoint={API_ENDPOINTS.SYSTEM_PRIORITY_TYPES}
                  columns={[
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                />
              ),
            },
            {
              key: 'intervention-status-types',
              label: '🔩 Stati Intervento',
              children: (
                <InterventionStatusTypeManager
                  title="Stato Intervento"
                  endpoint={API_ENDPOINTS.SYSTEM_INTERVENTION_STATUS_TYPES}
                />
              ),
            },
            {
              key: 'insurance-branch-types',
              label: '🛡️ Rami Sinistro',
              children: (
                <InsuranceBranchTypeManager
                  title="Ramo di Sinistro"
                  endpoint={API_ENDPOINTS.SYSTEM_INSURANCE_BRANCH_TYPES}
                />
              ),
            },
            {
              key: 'purchase-request-statuses',
              label: '📋 Stati RDO',
              children: (
                <SystemTableManager
                  title="Stato Richiesta Acquisto"
                  endpoint={API_ENDPOINTS.SYSTEM_PURCHASE_REQUEST_STATUSES}
                  /* Legge anche gli stati disattivati: altrimenti spegnerne uno
                     lo farebbe sparire da qui, e non si potrebbe più riaccendere. */
                  loadEndpoint={API_ENDPOINTS.SYSTEM_PURCHASE_REQUEST_STATUSES_ALL}
                  columns={[
                    { title: 'Ordine', dataIndex: 'ordine', key: 'ordine', width: 80, sorter: (a: any, b: any) => a.ordine - b.ordine, defaultSortOrder: 'ascend' as const },
                    { title: 'Codice', dataIndex: 'codice', key: 'codice', width: 180, render: (v: string | null) => v ? <code style={{ fontSize: 12 }}>{v}</code> : <span style={{ color: '#cf1322' }}>mancante</span> },
                    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 150 },
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'descrizione', ellipsis: true },
                    { title: 'Richiede commento', dataIndex: 'richiede_commento', key: 'richiede_commento', width: 130, render: (v: boolean) => v ? '✅' : '—' },
                    { title: 'Attivo', dataIndex: 'attivo', key: 'attivo', width: 80, render: (attivo: boolean) => attivo ? '✅' : '❌' },
                  ]}
                  extraFormFields={
                    <>
                      {/* Il codice è la chiave su cui /acquisti classifica lo stato.
                          Senza, un nuovo stato finisce in "Da verificare". */}
                      <Form.Item
                        name="codice"
                        label="Codice"
                        tooltip="Chiave stabile, minuscola e con gli underscore. Il codice non cambia quando il nome viene rinominato: è quello su cui il sistema classifica lo stato."
                        rules={[
                          { required: true, message: 'Codice obbligatorio (es: in_attesa_approvazione)' },
                          { pattern: /^[a-z][a-z0-9_]*$/, message: 'Solo minuscole, cifre e underscore' },
                        ]}
                      >
                        <Input placeholder="es: in_attesa_approvazione" />
                      </Form.Item>
                      <Form.Item name="richiede_commento" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>Richiede commento al cambio stato</Checkbox>
                      </Form.Item>
                    </>
                  }
                />
              ),
            },
            {
              key: 'integrazioni',
              label: '🔗 Integrazioni Esterne',
              children: <IntegrazioniSettings />,
            },
          ]} />
        </Card>
      )}

      {activeSection === 'marche-modelli' && (
        <Card title="Marche & Modelli personalizzati" style={{ marginBottom: 24 }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            Marche e modelli aggiunti qui appaiono in cima ai dropdown del form veicolo, prima dei dati CarQuery.
            Vengono aggiunti automaticamente anche quando salvi un veicolo con marca/modello non in lista.
          </p>
          <MarcheModelliSettings />
        </Card>
      )}

      {activeSection === 'activity-log' && (
        <ActivityLogPanel />
      )}

      {activeSection === 'adc-clausole' && (
        <Card title="Clausole contrattuali Auto di Cortesia">
          <AdcClausoleSettings />
        </Card>
      )}

      {/* User Form Modal */}
      <UserFormModal
        open={isModalOpen}
        editingUser={editingUser}
        onOk={handleModalOkValues}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingUser(null)
        }}
        loading={false}
      />
    </div>
  )
}

export default SettingsPage
